/* eslint-disable @tencent/i18next/no-literal-string */
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import {
    IElementNode,
    ITextBlockParentBoxNode,
    ITextParagraphBoxNode,
} from '@src/slide/application/service/slide-rating-and-improve/slide-content-analyzer.interface';
import { ContentRect } from '@src/slide/application/service/slide-content-layout-analyzer/core/algorithm/data-structure/content-rect';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { FillType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { BorderType } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { isEditorEmpty, isParaEmpty } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import {
    centerPointOfRect,
    checkRectInOtherRectSide, IRect,
    rectIntersection,
} from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import { getFrameAfterRotate } from '@src/slide/infrastructure/module/independent/graphic';
import { twipsToPx, IParagraphBox } from '@tencent/alloy-editor';
import { SeparatorRect, SeparatorType } from './data-structure/separator-rect';
import { ContentTree, ContentTreeNode, ContentTreeNodeType, NodeChildrenStructType } from './data-structure/content-tree';
import { VisualBlock, VisualBlockFeatureType } from './data-structure/visual-block';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';

/**
 * 分析内容布局信息
 */
export class VipsContentLayoutAnalyzer {
    public currentContentTree: ContentTree | undefined;
    public contentTreeList: Array<ContentTree>;
    private presentation: IPresentationModel;
    private currentElement: IElementNode | undefined;
    private separatorList: Array<SeparatorRect>;
    private visualBlockList: Array<VisualBlock>;
    /**
     * 当前要拆分的子区域
     * @private
     */
    private currentContentNode: ContentTreeNode;
    private currentContentRect: IRect;

    public reset() {
        this.contentTreeList = [];
        this.currentContentTree = undefined;
        this.currentElement = undefined;
        this.separatorList = [];
        this.visualBlockList = [];
    }

    public resetWithPresentation(presentation: IPresentationModel) {
        this.reset();
        this.presentation = presentation;
    }

    public analysisNewSlide(slideModel: ISlideModel, slideIndex: number) {
        this.visualBlockList = [];
    }

    public analysisSlideEnd(slideModel: ISlideModel, slideIndex: number) {
        // 初始化语义树和对应的根结点
        this.currentContentTree = new ContentTree(slideModel);
        const rect: IRect = {
            left: 0, top: 0,
            width: this.presentation.props.slideWidth, height: this.presentation.props.slideHeight,
        };
        const root = new ContentTreeNode(rect);
        root.visualBlocks = [...this.visualBlockList];
        this.currentContentTree.children.push(root);
        this.setVisualBlocksIndex();
        this.doAnalysisRecursively(root);
        this.contentTreeList.push(this.currentContentTree);
    }

    /**
     * 遇到一个新的元素，添加元素到矩形数组中
     * @param elementNode
     * @param options noAnalysisShape: 不分析普通图形
     */
    // eslint-disable-next-line complexity
    public analysisElement(elementNode: IElementNode, options: { noAnalysisShape?: boolean } = {}) {
        const contentNode = new ContentRect(elementNode);
        if (contentNode.polygon.vertices[0].x <= 0
            && contentNode.polygon.vertices[0].y <= 0
            && contentNode.polygon.vertices[1].x >= this.presentation.props.slideWidth
            && contentNode.polygon.vertices[1].y <= 0
            && contentNode.polygon.vertices[2].x >= this.presentation.props.slideWidth
            && contentNode.polygon.vertices[2].y >= this.presentation.props.slideHeight
            && contentNode.polygon.vertices[3].x <= 0
            && contentNode.polygon.vertices[3].y >= this.presentation.props.slideHeight
        ) {
            // 在页面外面的内容不用分析
            return ;
        }
        if (!elementNode.element.isFromSlide) {
            // master和layout上的暂时不处理
            return ;
        }
        // 不可见元素不处理
        if (elementNode.element.hidden) return;

        const boundingRect = elementNode.element.getFrame().boundingRectOnSlide();
        if (boundingRect.left > this.presentation.props.slideWidth * 1.2) return;
        if (boundingRect.top > this.presentation.props.slideHeight * 1.2) return;
        if (boundingRect.left + boundingRect.width < -this.presentation.props.slideWidth * 0.2) return;
        if (boundingRect.top + boundingRect.height < -this.presentation.props.slideHeight * 0.2) return;

        if (elementNode.element.type === UIModelType.SHAPE) {
            const shape = elementNode.element as IShapeModel;
            const fillType = shape.getBackgroundFill().getShownFillType();
            const borderType = shape.getBorder().getBorderType();
            let hasFill = false;
            let hasBorder = false;
            if (fillType && fillType !== FillType.NoFill) hasFill = true;
            if (borderType && borderType !== BorderType.NoFill) hasBorder = true;
            if (hasFill || hasBorder) {
                // TODO 如果超出可见区域应该不创建忽略
                const block = new VisualBlock(boundingRect, elementNode);
                block.degreeOfCoherence = 10;
                if (!options.noAnalysisShape) {
                    this.visualBlockList.push(block);
                }
                if (isEditorEmpty(shape.paragraphList)) {
                    block.degreeOfCoherence = 9;
                } else {
                    this.currentElement = elementNode;
                }
                return;
            }
            this.currentElement = elementNode;
        } else if (elementNode.element.type === UIModelType.TABLE
            || elementNode.element.type === UIModelType.CHART
            || elementNode.element.type === UIModelType.CONNECTOR
            || elementNode.element.type === UIModelType.IMAGE
        ) {
            // TODO 如果超出可见区域应该不创建忽略
            // if (elementNode.element.type === UIModelType.CONNECTOR) {
            //     const connectorModel = (elementNode.element as IConnectorModel);
            //     if (connectorModel.getFrame().getHeight() <= 0 || connectorModel.getFrame().getWidth() <= 0) {
            //         const lineType = connectorModel.getGraphic().getGraphicLinInfo()?.bgLine;
            //         if (lineType === LineType.LINE || lineType === LineType.STRAIGNTCONNECTOR1) {
            //             if (canvasToPt(Number(connectorModel.getBorder().getBorderInfo()?.width)) < 20) {
            //                 // 水平或者垂直连接线忽略
            //                 return;
            //             }
            //         }
            //     }
            // }
            const block = new VisualBlock(boundingRect, elementNode);
            block.degreeOfCoherence = 10;
            if (!options.noAnalysisShape) {
                this.visualBlockList.push(block);
            }
            return;
        } else if (elementNode.element.type === UIModelType.GROUP) {
            const groupModel = elementNode.element as IGroupModel;
            // TODO 连续的自定义图形应该合并，这里需要调整
        }
    }

    // eslint-disable-next-line complexity
    public analysisBlockParentBox(textBlockParentBoxNode: ITextBlockParentBoxNode) {
        // 形状要处理的时候才需要进一步拿文本的信息
        if (textBlockParentBoxNode.element !== this.currentElement?.element) return;
        if (!this.currentElement) return;
        if (this.currentElement.element.type === UIModelType.SHAPE) {
            const shape = this.currentElement.element as IShapeModel;
            const fillType = shape.getBackgroundFill().getShownFillType();
            const borderType = shape.getBorder().getBorderType();
            let hasFill = false;
            let hasBorder = false;
            if (fillType && fillType !== FillType.NoFill) hasFill = true;
            if (borderType && borderType !== BorderType.NoFill) hasBorder = true;
            if (isEditorEmpty((this.currentElement.element as IShapeModel).paragraphList)) {
                return;
            }
            if (hasBorder || hasFill) {
                this.visualBlockList[this.visualBlockList.length - 1].info = textBlockParentBoxNode;
                this.currentElement = undefined;
            }
        }
    }

    public analysisTextParagraph(textParagraphBoxNode: ITextParagraphBoxNode) {
        // 形状要处理的时候才需要进一步拿文本的信息
        if (textParagraphBoxNode.element !== this.currentElement?.element) return;
        if (textParagraphBoxNode.element.type !== UIModelType.SHAPE) return;
        if (!this.currentElement) return;
        const paraIndex = textParagraphBoxNode.textBoxPath[textParagraphBoxNode.textBoxPath.length - 1];
        const paragraph = (textParagraphBoxNode.element as IShapeModel).paragraphList?.[paraIndex];
        if (paragraph && !isParaEmpty(paragraph)) {
            const frameOnSlide = textParagraphBoxNode.element.getFrame().frameOnSlide();
            const textRelativeRect = { ...textParagraphBoxNode.paraBox.rect };
            const lineBounds = this.getRealBoundsFromTextParagraph(textParagraphBoxNode.paraBox);
            if (textParagraphBoxNode.paraBox.parentBox) {
                textRelativeRect.x += textParagraphBoxNode.paraBox.parentBox.rect.x + lineBounds.left;
                textRelativeRect.y += textParagraphBoxNode.paraBox.parentBox.rect.y + lineBounds.top;
            }
            const textRect: IRect = {
                left: twipsToPx(textRelativeRect.x) + frameOnSlide.left + textParagraphBoxNode.textRect.left,
                top: twipsToPx(textRelativeRect.y) + frameOnSlide.top + textParagraphBoxNode.textRect.top,
                width: twipsToPx(lineBounds.width),
                height: twipsToPx(lineBounds.height),
            };
            const boundingRect = getFrameAfterRotate(textRect, frameOnSlide.rotate, centerPointOfRect(frameOnSlide));
            const block = new VisualBlock(boundingRect, textParagraphBoxNode);
            block.degreeOfCoherence = 10;
            this.visualBlockList.push(block);
        }
    }

    /**
     * 递归分析
     * @private
     */
    private doAnalysisRecursively(node: ContentTreeNode) {
        this.doAnalysisContent(node);
        if (node.children.length > 0) {
            node.children.forEach((childNode) => {
                childNode.parent = node;
                this.doAnalysisRecursively(childNode);
            });
        }
    }

    private doAnalysisContent(node: ContentTreeNode, withAddToCurrentNode = true) {
        if (node.visualBlocks.length <= 1) {
            return;
        }
        this.separatorList = [];
        this.currentContentNode = node;
        this.currentContentRect = node.getRect();
        const visualBlockList = [...node.visualBlocks];
        // 这里要从水平和垂直方向都做检测
        // 水平检测
        const horSeparatorList = this.visualSeparatorDetection(SeparatorType.Horizontal);
        const horizontalResult = this.contentStructureConstruction(horSeparatorList);
        // 垂直检测
        const verSeparatorList = this.visualSeparatorDetection(SeparatorType.Vertical);
        const verticalResult = this.contentStructureConstruction(verSeparatorList);
        // 选出合适的检测结果
        const result = this.selectRightSeparatorResult(
            horSeparatorList, horizontalResult,
            verSeparatorList, verticalResult,
        );
        if (!result) {
            // 超过15层没什么意义了，退出
            if (node.getDepth() > 15) return;
            // 处理无法找出分隔条的情况
            if (withAddToCurrentNode) {
                const noSeparatorResult = this.handleNoSeparator(visualBlockList);
                node.type = noSeparatorResult.parentNodeType;
                node.children = noSeparatorResult.nodes;
                node.childrenStructType = noSeparatorResult.structType;
            }
        } else {
            this.separatorList = result.separatorList;
            if (withAddToCurrentNode) {
                // 分隔条和页面块的方向是相反的
                node.type = result.separatorType === SeparatorType.Horizontal
                    ? ContentTreeNodeType.Vertical : ContentTreeNodeType.Horizontal;
                node.children = result.contentTreeNodes;
                node.childrenStructType = result.structType;
            }
        }
    }

    /**
     * 选择合适的检测结果
     * @param horSeparatorList
     * @param horizontalResult
     * @param verSeparatorList
     * @param verticalResult
     * @private
     */
    private selectRightSeparatorResult(
        horSeparatorList: Array<SeparatorRect>, horizontalResult: Array<ContentTreeNode>,
        verSeparatorList: Array<SeparatorRect>, verticalResult: Array<ContentTreeNode>,
    ): {
        contentTreeNodes: Array<ContentTreeNode>,
        separatorList: Array<SeparatorRect>,
        separatorType: SeparatorType,
        structType: NodeChildrenStructType
    } | undefined {
        if (horSeparatorList.length < 1 && verSeparatorList.length < 1) return undefined;
        const horStructType = ContentTreeNode.getContentNodesStructType(horizontalResult, SeparatorType.Horizontal);
        const verStructType = ContentTreeNode.getContentNodesStructType(verticalResult, SeparatorType.Vertical);
        if (horSeparatorList.length < 1 || verSeparatorList.length < 1) {
            if (horSeparatorList.length > 0) {
                return {
                    contentTreeNodes: horizontalResult,
                    separatorList: horSeparatorList,
                    separatorType: SeparatorType.Horizontal,
                    structType: horStructType,
                };
            }
            if (verSeparatorList.length > 0) {
                return {
                    contentTreeNodes: verticalResult,
                    separatorList: verSeparatorList,
                    separatorType: SeparatorType.Vertical,
                    structType: verStructType,
                };
            }
        } else {
            // 两个方向分隔条结果长度都大于0
            if (horStructType === NodeChildrenStructType.Parallel) {
                return {
                    contentTreeNodes: horizontalResult,
                    separatorList: horSeparatorList,
                    separatorType: SeparatorType.Horizontal,
                    structType: horStructType,
                };
            }
            if (verStructType === NodeChildrenStructType.Parallel) {
                return {
                    contentTreeNodes: verticalResult,
                    separatorList: verSeparatorList,
                    separatorType: SeparatorType.Vertical,
                    structType: verStructType,
                };
            }
            return {
                contentTreeNodes: horizontalResult,
                separatorList: horSeparatorList,
                separatorType: SeparatorType.Horizontal,
                structType: horStructType,
            };
        }
    }

    private setVisualBlocksIndex() {
        this.visualBlockList.forEach((block, index) => {
            block.listIndex = index;
        });
    }

    /**
     * 分割条检测
     * @private
     */
    private visualSeparatorDetection(separatorType: SeparatorType): Array<SeparatorRect> {
        const separatorList: Array<SeparatorRect> = [];
        const separator = new SeparatorRect(
            separatorType, { ...this.currentContentRect },
            this.currentContentRect,
        );
        separatorList.push(separator);
        // 先处理水平分隔条
        this.currentContentNode.visualBlocks.forEach((block) => {
            let i = 0;
            while (i < separatorList.length) {
                if (this.rectInRectWithType(block.rect, separatorList[i].rect, separatorType)) {
                    // 页面块在分隔条里面，需要拆分分隔条变成两个
                    this.splitSeparator(separatorList[i], block, i, separatorList);
                    i = i + 2;
                } else if (this.rectIntersectionWithType(
                    block.rect, separatorList[i].rect,
                    separatorType,
                )) {
                    // 页面块和分隔条相交，查找所有相交的分隔条
                    let t = 0;
                    while (i + t < separatorList.length
                    && this.rectIntersectionWithType(
                        block.rect, separatorList[i + t].rect,
                        separatorType,
                    )) {
                        t = t + 1;
                    }
                    this.changeSeparator(i, t, block, separatorType, separatorList);
                }
                i = i + 1;
            }
        });
        this.removeAroundSeparator(separatorList, this.currentContentNode);
        this.setSeparatorBlocks(separatorList, this.currentContentNode);
        this.removeAroundConnectorSeparator(separatorList);
        return separatorList;
    }

    /**
     * block在分隔条里面，把分隔条拆分为两个
     * @param separator
     * @param visualBlock
     * @param index
     * @param separatorList
     * @private
     */
    private splitSeparator(
        separator: SeparatorRect, visualBlock: VisualBlock,
        index: number, separatorList: Array<SeparatorRect>,
    ) {
        if (separator.type === SeparatorType.Horizontal) {
            const rect = { ...this.currentContentRect };
            rect.top = visualBlock.rect.top + visualBlock.rect.height;
            rect.height = separator.separatorBlock2.rect.top - visualBlock.rect.top - visualBlock.rect.height;
            const newSeparator = new SeparatorRect(SeparatorType.Horizontal, rect, this.currentContentRect);
            newSeparator.separatorBlock1 = visualBlock;
            newSeparator.separatorBlock2 = separator.separatorBlock2;
            separatorList.splice(index + 1, 0, newSeparator);

            separator.separatorBlock2 = visualBlock;
            separator.rect.height = visualBlock.rect.top
                - separator.separatorBlock1.rect.top - separator.separatorBlock1.rect.height;
        } else {
            const rect = { ...this.currentContentRect };
            rect.left = visualBlock.rect.left + visualBlock.rect.width;
            rect.width = separator.separatorBlock2.rect.left - visualBlock.rect.left - visualBlock.rect.width;
            const newSeparator = new SeparatorRect(SeparatorType.Vertical, rect, this.currentContentRect);
            newSeparator.separatorBlock1 = visualBlock;
            newSeparator.separatorBlock2 = separator.separatorBlock2;
            separatorList.splice(index + 1, 0, newSeparator);

            separator.separatorBlock2 = visualBlock;
            separator.rect.width = visualBlock.rect.left
                - separator.separatorBlock1.rect.left - separator.separatorBlock1.rect.width;
        }
    }

    /**
     * 根据页面块和分隔条的相交来看怎么调整已有的分隔条
     * @param index
     * @param num
     * @param block
     * @param type
     * @param separatorList
     * @private
     */
    private changeSeparator(
        index: number, num: number, block: VisualBlock,
        type: SeparatorType, separatorList: Array<SeparatorRect>,
    ) {
        const name1 = type === SeparatorType.Horizontal ? 'top' : 'left';
        const name2 = type === SeparatorType.Horizontal ? 'height' : 'width';

        if (num === 1) {
            // 只影响到一个分隔条，看相交的方式来进行对应的处理
            if (block.rect[name1] < separatorList[index].separatorBlock1.rect[name1]
                + separatorList[index].separatorBlock1.rect[name2]) {
                // 页面块在分隔条上面
                if (block.rect[name1] + block.rect[name2] < separatorList[index].separatorBlock2.rect[name1]) {
                    separatorList[index].separatorBlock1 = block;
                    separatorList[index].rect[name1] = block.rect[name1] + block.rect[name2];
                    separatorList[index].rect[name2] = separatorList[index].separatorBlock2.rect[name1]
                        - separatorList[index].rect[name1];
                } else {
                    separatorList.splice(index, 1);
                }
            } else {
                // 页面块在分隔条下面，调整第index个分隔条的高度
                separatorList[index].separatorBlock2 = block;
                separatorList[index].rect[name2] = block.rect[name1] - separatorList[index].rect[name1];
            }
            return ;
        }
        if (num > 1) {
            // 影响到的分隔条大于1，调整第index个分隔条的高度和
            separatorList[index].separatorBlock2 = block;
            separatorList[index].rect[name2] = block.rect[name1] - separatorList[index].rect[name1];
            if (this.rectInRectWithType(separatorList[index + num - 1].rect, block.rect, type)) {
                // 如果分隔条在页面块内，删除掉即可
                separatorList.splice(index + num - 1, 1);
            } else {
                // 调整最后一个分隔条的top
                separatorList[index + num - 1].separatorBlock1 = block;
                separatorList[index + num - 1].rect[name1] = block.rect[name1] + block.rect[name2];
                separatorList[index + num - 1].rect[name2] = separatorList[index + num - 1]
                    .separatorBlock2.rect[name1] - block.rect[name1] - block.rect[name2];
            }
        }
        if (num > 2) {
            // 删除中间的，两边的已经处理过了
            separatorList.splice(index + 1, num - 2);
        }
        if (separatorList[index].rect[name2] < 0) {
            separatorList.splice(index, 1);
        }
    }

    /**
     * 删除周围一圈的分隔条
     * @private
     */
    private removeAroundSeparator(separatorList: Array<SeparatorRect>, currentContentNode: ContentTreeNode) {
        const { length } = separatorList;
        const currentRect = currentContentNode.getRect();
        for (let i = length - 1; i >= 0; i--) {
            const separator = separatorList[i];
            // 如果贴着边，并且边上没有
            if (checkRectInOtherRectSide(separator.rect, currentRect)
                && (!separator.separatorBlock1.info || !separator.separatorBlock2.info)) {
                separatorList.splice(i, 1);
            }
        }
    }

    /**
     * 删除周围一圈的连接线分隔条
     * @private
     */
    private removeAroundConnectorSeparator(separatorList: Array<SeparatorRect>) {
        const { length } = separatorList;
        for (let i = length - 1; i >= 0; i--) {
            const separator = separatorList[i];
            // 如果贴着边，并且边上没有
            if (separator.allBlocks1.length === 0 || separator.allBlocks2.length === 0) {
                separatorList.splice(i, 1);
            }
        }
    }

    /**
     * 设置separator的相邻页面块
     * @private
     */
    // eslint-disable-next-line complexity
    private setSeparatorBlocks(separatorList: Array<SeparatorRect>, currentContentNode: ContentTreeNode) {
        if (separatorList.length < 1) return;
        const { type } = separatorList[0];
        const name1 = type === SeparatorType.Horizontal ? 'top' : 'left';
        const name2 = type === SeparatorType.Horizontal ? 'height' : 'width';
        currentContentNode.visualBlocks.sort((a, b) => a.rect[name1] - b.rect[name1]);
        for (let i = 0; i < currentContentNode.visualBlocks.length; i++) {
            const visualBlock = currentContentNode.visualBlocks[i];
            for (let j = 0; j < separatorList.length; j++) {
                if (separatorList[j].rect[name1] - (visualBlock.rect[name1] + visualBlock.rect[name2]) > -1e-5) {
                    // 在分隔条上面
                    if (j === 0) {
                        // 在第一个的上面
                        if (!separatorList[j].blocks1.includes(visualBlock)) {
                            separatorList[j].blocks1.push(visualBlock);
                        }
                        break;
                    } else if (visualBlock.rect[name1] - (separatorList[j - 1].rect[name1]
                        + separatorList[j - 1].rect[name2]) > -1e-5) {
                        // 在两个中间
                        if (!separatorList[j - 1].blocks2.includes(visualBlock)) {
                            separatorList[j - 1].blocks2.push(visualBlock);
                        }
                        if (!separatorList[j].blocks1.includes(visualBlock)) {
                            separatorList[j].blocks1.push(visualBlock);
                        }
                        break;
                    }
                } else if (visualBlock.rect[name1] - (separatorList[j].rect[name1]
                    + separatorList[j].rect[name2]) > -1e-5) {
                    // 在分隔条下面
                    if (j === separatorList.length - 1) {
                        // 在最后一个的下面
                        if (!separatorList[j].blocks2.includes(visualBlock)) {
                            separatorList[j].blocks2.push(visualBlock);
                        }
                        break;
                    }
                }
            }
        }
        for (let i = 0; i < currentContentNode.visualBlocks.length; i++) {
            const visualBlock = currentContentNode.visualBlocks[i];
            for (let j = 0; j < separatorList.length; j++) {
                if (separatorList[j].rect[name1] - (visualBlock.rect[name1] + visualBlock.rect[name2]) > -1e-5) {
                    // 在分隔条上面
                    if (!separatorList[j].allBlocks1.includes(visualBlock)) {
                        separatorList[j].allBlocks1.push(visualBlock);
                    }
                } else if (visualBlock.rect[name1] - (separatorList[j].rect[name1]
                    + separatorList[j].rect[name2]) > -1e-5) {
                    // 在分隔条下面
                    if (!separatorList[j].allBlocks2.includes(visualBlock)) {
                        separatorList[j].allBlocks2.push(visualBlock);
                    }
                }
            }
        }
    }

    /**
     * 内容结构构建
     * @private
     */
    // eslint-disable-next-line complexity
    private contentStructureConstruction(separatorList: Array<SeparatorRect>): Array<ContentTreeNode> {
        if (separatorList.length < 1) {
            return [];
        }
        const { type } = separatorList[0];
        const sortSeparatorList = [...separatorList];
        const name = this.getNameFromType(type);
        // 根据分隔条权重排序，大的在前面
        sortSeparatorList.sort((a, b) => b.getWeight() - a.getWeight());
        // 最大权重的分隔条
        const maxWeight = separatorList[0].getWeight();
        // 只留下权重最大的分隔条数组，并且将页面块重新划分
        let i = 0;
        let aliveNum = 0; // 上一个需要保留的分隔条坐标
        while (i < separatorList.length) {
            if (separatorList[i].getWeight() === maxWeight) {
                const separator = separatorList[i];
                if (aliveNum === 0) {
                    if (i > 0) {
                        // i为0时无需调整，只处理大于0的情况就好
                        separator.separatorBlock1 = separatorList[0].separatorBlock1;
                        separator.rect[name.topOrLeft] = separator.separatorBlock1.rect[name.topOrLeft]
                            + separator.separatorBlock1.rect[name.heightOrWidth];
                        separator.rect[name.heightOrWidth] = separator.separatorBlock2.rect[name.topOrLeft]
                            - separator.rect[name.topOrLeft];
                        // 调整block1里的页面块数组
                        for (let j = i - 1; j >= 0; j--) {
                            separator.blocks1 = separatorList[j].blocks1.concat(separator.blocks1);
                        }
                        // 删除之前的分隔条
                        separatorList.splice(0, i);
                        i = 0;
                    }
                } else {
                    // 不是第一个，调整相邻的页面块
                    separator.separatorBlock1 = separatorList[aliveNum - 1].separatorBlock2;
                    separator.rect[name.topOrLeft] = separator.separatorBlock1.rect[name.topOrLeft]
                        + separator.separatorBlock1.rect[name.heightOrWidth];
                    separator.rect[name.heightOrWidth] = separator.separatorBlock2.rect[name.topOrLeft]
                        - separator.rect[name.topOrLeft];
                    // 调整block1里的页面块数组
                    for (let j = i - 1; j >= aliveNum; j--) {
                        separator.blocks1 = separatorList[j].blocks1.concat(separator.blocks1);
                    }
                    separatorList[aliveNum - 1].blocks2 = separator.blocks1;
                    // 删除中间的分隔条
                    separatorList.splice(aliveNum, i - aliveNum);
                    i = aliveNum;
                }
                aliveNum += 1;
            }
            i += 1;
        }
        if (i > aliveNum) {
            let blocks: VisualBlock[] = [...separatorList[i - 1].blocks2];
            // 调整block1里的页面块数组
            for (let j = i - 1; j >= aliveNum; j--) {
                blocks = separatorList[j].blocks1.concat(blocks);
            }
            separatorList[aliveNum - 1].blocks2 = blocks;
            // 删除中间的分隔条
            separatorList.splice(aliveNum, i - aliveNum);
        }
        const children: Array<ContentTreeNode> = [];
        for (let j = 0; j < separatorList.length; j++) {
            if (j === 0) {
                const childTreeNode = new ContentTreeNode();
                childTreeNode.visualBlocks = [...separatorList[j].blocks1];
                children.push(childTreeNode);
            }
            const childTreeNode = new ContentTreeNode();
            childTreeNode.visualBlocks = [...separatorList[j].blocks2];
            children.push(childTreeNode);
        }
        return children;
    }

    /**
     * 没有分隔条，尝试删除index值比较低的页面块
     * @private
     */
    // eslint-disable-next-line complexity
    private handleNoSeparator(visualBlockList: Array<VisualBlock>): {
        nodes: Array<ContentTreeNode>,
        structType: NodeChildrenStructType,
        parentNodeType: ContentTreeNodeType,
    } {
        visualBlockList.sort((a, b) => Number(a.listIndex) - Number(b.listIndex));
        const visualBlocks = [...visualBlockList];
        // 已经遍历过的有文字的页面块
        const hasTextVisualBlocks: VisualBlock[] = [];
        // 已经遍历过和之前元素不会重合的页面块
        const noCrossTextBlocks: VisualBlock[] = [];
        const crossTextBlocks: VisualBlock[] = [];
        // 遍历页面块来尝试当成底层, 尝试10个已经足够了
        for (let i = 0; i < visualBlocks.length && i < 10; i++) {
            if (visualBlocks[i].hasText()) {
                hasTextVisualBlocks.push(visualBlocks[i]);
            } else {
                // 如果和已有的带文本的有重合区域的话则不应该
                let noCross = true;
                for (let j = 0; j < hasTextVisualBlocks.length; j++) {
                    if (rectIntersection(visualBlocks[i].rect, hasTextVisualBlocks[j].rect)) {
                        noCross = false;
                        break;
                    }
                }
                if (noCross) {
                    // 如果当前页面块和之前的文本页面块没有重合，可以也当作底层。
                    const childTreeNode = new ContentTreeNode();
                    // 后面的页面块以及不和前面的文字重合的页面块
                    childTreeNode.visualBlocks = visualBlocks
                        .filter((block, index) => index > i
                                || (index < i && !noCrossTextBlocks.includes(block)));
                    // 看把没有文字交叉的页面块当背景的话，剩下的内容是否能取到分隔条
                    this.doAnalysisContent(childTreeNode, false);
                    // 取到了分隔条
                    if (this.separatorList.length > 0) {
                        // 找到合适的子节点了，直接返回
                        return {
                            nodes: [childTreeNode],
                            structType: NodeChildrenStructType.Nested,
                            parentNodeType: ContentTreeNodeType.None,
                        };
                    }
                    // 没有找到分隔条，把不交叉的文字块放入数组
                    noCrossTextBlocks.push(visualBlocks[i]);
                } else {
                    // 把会和之前文字交叉的页面块放入数组
                    crossTextBlocks.push(visualBlocks[i]);
                }
            }
        }
        // 找到第一个不是文字，并且不跨越别的文字块的块
        const blockIndex = visualBlocks
            .findIndex(value => !(hasTextVisualBlocks.includes(value)
                || crossTextBlocks.includes(value)));
        if (blockIndex > -1) {
            // 找到可以当背景的页面块
            const backChildTreeNode = new ContentTreeNode();
            const childTreeNode = new ContentTreeNode();
            backChildTreeNode.visualBlocks = [visualBlocks[blockIndex]];
            // 之前的文字页面块和之后的页面块
            const otherBlocks = visualBlocks.filter((block, index) => index !== blockIndex);
            childTreeNode.visualBlocks = otherBlocks;
            if (otherBlocks.length === 1) {
                if (backChildTreeNode.visualBlocks[0]
                    .isSimilarToOther(otherBlocks[0], [VisualBlockFeatureType.Frame])) {
                    const frame1 = visualBlocks[0].getFrame();
                    const frame2 = visualBlocks[1].getFrame();
                    let parentNodeType = ContentTreeNodeType.None;
                    if (Math.abs(Number(frame1?.left) - Number(frame2?.left)) < 30) {
                        parentNodeType = ContentTreeNodeType.Horizontal;
                    } else {
                        parentNodeType = ContentTreeNodeType.Vertical;
                    }
                    return {
                        nodes: [backChildTreeNode, childTreeNode],
                        structType: NodeChildrenStructType.Parallel,
                        parentNodeType,
                    };
                }
            } else {
                return {
                    nodes: [backChildTreeNode, childTreeNode],
                    structType: NodeChildrenStructType.Nested,
                    parentNodeType: ContentTreeNodeType.None,
                };
            }
        }
        // 真的没找到可以当背景的块，给一个嵌套的结果吧
        const backChildTreeNode = new ContentTreeNode();
        const childTreeNode = new ContentTreeNode();
        backChildTreeNode.visualBlocks = [visualBlocks[0]];
        // 之前的文字页面块和之后的页面块
        const otherBlocks = visualBlocks.filter((block, index) => index !== 0);
        childTreeNode.visualBlocks = otherBlocks;
        if (otherBlocks.length > 0) {
            return {
                nodes: [backChildTreeNode, childTreeNode],
                structType: NodeChildrenStructType.Nested,
                parentNodeType: ContentTreeNodeType.None,
            };
        }
        return {
            nodes: [backChildTreeNode],
            structType: NodeChildrenStructType.Nested,
            parentNodeType: ContentTreeNodeType.None,
        };
    }

    /**
     * 在指定方向上被包含
     * @param rect1
     * @param rect2
     * @param type
     * @private
     */
    private rectInRectWithType(rect1: IRect, rect2: IRect, type: SeparatorType): boolean {
        if (type === SeparatorType.Horizontal) {
            return (rect1.top - rect2.top) > -1e-5 && (rect2.top + rect2.height) - (rect1.top + rect1.height) > -1e-5;
        }
        return rect1.left - rect2.left > -1e-5 && (rect2.left + rect2.width) - (rect1.left + rect1.width) > -1e-5;
    }

    /**
     * 在指定方向上相交
     * @param rect1
     * @param rect2
     * @param type
     * @private
     */
    private rectIntersectionWithType(rect1: IRect, rect2: IRect, type: SeparatorType): boolean {
        if (type === SeparatorType.Horizontal) {
            return !(rect2.top - (rect1.top + rect1.height) > -1e-5 || rect1.top - (rect2.top + rect2.height) > -1e-5);
        }
        return !(rect2.left - (rect1.left + rect1.width) > -1e-5 || rect1.left - (rect2.left + rect2.width) > -1e-5);
    }

    private getNameFromType(type: SeparatorType): { topOrLeft: string, heightOrWidth: string } {
        const topOrLeft = type === SeparatorType.Horizontal ? 'top' : 'left';
        const heightOrWidth = type === SeparatorType.Horizontal ? 'height' : 'width';
        return {
            topOrLeft,
            heightOrWidth,
        };
    }

    private getRealBoundsFromTextParagraph(para: IParagraphBox): IRect {
        if (para.children.length <= 0) return { left: 0, top: 0, width: 0, height: 0 };
        const lineBoundingRect = { left: Infinity, top: Infinity, width: 0, height: 0 };
        let maxRight = 0;
        let maxBottom = 0;
        para.children.forEach((line) => {
            lineBoundingRect.left = Math.min(lineBoundingRect.left, line.rect.x);
            lineBoundingRect.top = Math.min(lineBoundingRect.top, line.rect.y);
            maxRight = Math.max(maxRight, line.rect.x + line.rect.width);
            maxBottom = Math.max(maxBottom, line.rect.y + line.rect.height);
        });
        lineBoundingRect.width = maxRight - lineBoundingRect.left;
        lineBoundingRect.height = maxBottom - lineBoundingRect.top;
        return lineBoundingRect;
    }
}
